import React, { useState, useEffect, useMemo, useRef, ChangeEvent, KeyboardEvent, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Select, { SingleValue, components } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import { useReactTable, getCoreRowModel, createColumnHelper } from "@tanstack/react-table";
import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import StatCard from "./ReportsCards";
import { getAuthCookie } from "../utils/authCookies";

type CardsSummary = {
    total_orders: number;
    total_revenue: number;
    amount_paid: number;
    outstanding_balance: number;
};


interface InstallationSummaryRow {
    brokerage_firm: string;
    office: string;
    office_admin: string;
    agent_name: string;
    installations: number;
    sales_orders: number;
    total_amount: number;
    paid: number;
    balance: number;
    over_paid: boolean;
}

const DATE_OPTIONS = [
    { label: "Last 7 Days", value: "last_7_days" },
    { label: "Last 15 Days", value: "last_15_days" },
    { label: "Last 30 Days", value: "last_30_days" },
    { label: "Custom", value: "custom" }
];

const PAYMENT_STATUS_OPTIONS = [
    { label: "All", value: "all" },
    { label: "Paid", value: "Paid" },
    { label: "Partial Payment", value: "Partial Payment" },
    { label: "Pending", value: "Pending" },
    { label: "Overdue", value: "Overdue" },
];

const PAYMENT_METHOD_OPTIONS = [
    { label: "Card", value: "Card" },
    { label: "Net 30", value: "Net 30" },
    { label: "Stripe", value: "Stripe" }
];

type SelectOption = {
    label: string;
    value: string;
};

// Select2 types for brokerage firms
type BrokerageOption = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
};

const DropdownIndicator = (props: any) => {
    if (props.selectProps.isDisabled) return null;
    return (
        <components.DropdownIndicator {...props}>
            <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
        </components.DropdownIndicator>
    );
};

const customSelectStyles = {
  indicatorsContainer: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  dropdownIndicator: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  indicatorSeparator: () => ({ display: "none" }),
  control: (p: any, s: any) => ({
    ...p,
    backgroundColor: s.isDisabled ? "#f2f2f2" : p.backgroundColor,
    cursor: s.isDisabled ? "not-allowed" : "pointer",
    opacity: 1,
  }),
  singleValue: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  valueContainer: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  input: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color, WebkitTextFillColor: s.isDisabled ? "#000" : undefined }),
  placeholder: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  multiValue: (p: any, s: any) => ({ ...p, backgroundColor: s.isDisabled ? "#e5e7eb" : p.backgroundColor }),
  multiValueLabel: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  multiValueRemove: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : p.display }),
  // Fix dropdown menu positioning to appear below the input
  menu: (provided: any) => ({
    ...provided,
    zIndex: 9999,
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
  }),
  menuList: (provided: any) => ({
    ...provided,
    maxHeight: "200px",
  }),
};

const agentSelectStyles = {
  ...customSelectStyles,
  menu: (provided: any) => ({
    ...customSelectStyles.menu(provided),
    minWidth: "220px",
  }),
  menuList: (provided: any) => ({
    ...customSelectStyles.menuList(provided),
    overflowX: "hidden",
  }),
  option: (provided: any) => ({
    ...provided,
    whiteSpace: "normal",
    wordBreak: "break-word",
  }),
};


export default function InstallationSummaryReport({ report }: { report: string }) {
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const location = useLocation();
    
    // Detect login type
    const isSignageTeammember = location.pathname.includes('/signageteammember');
    
    // State for parent signage firm (for signageteammember login)
    const [parentSignageId, setParentSignageId] = useState<string | null>(null);
    const [parentSignageName, setParentSignageName] = useState<string | null>(null);
    
    const [data, setData] = useState([]);
    const [datePreset, setDatePreset] = useState(DATE_OPTIONS[2]); // Default: Last 30 Days
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [paymentStatus, setPaymentStatus] = useState(PAYMENT_STATUS_OPTIONS[0]); // All
    const [paymentMethod, setPaymentMethod] = useState([PAYMENT_METHOD_OPTIONS[0]]); // Default: Card
    const [selectedBrokerage, setSelectedBrokerage] = useState<SelectOption>();
    const [selectedOffice, setSelectedOffice] = useState<SelectOption | null>(null);
    const [selectedAgent, setSelectedAgent] = useState<SelectOption | null>(null);
    const [error, setError] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [cards, setCards] = useState<CardsSummary | null>(null);
    // Dropdown options
    const [officeOptions, setOfficeOptions] = useState([]);
    const [agentOptions, setAgentOptions] = useState([]);
    const [signageAgentOptions, setSignageAgentOptions] = useState([]);
    
    // State for AsyncPaginate selected options
    const [selectedBrokerageOption, setSelectedBrokerageOption] = useState<BrokerageOption | null>(null);
    
    // Fetch parent hierarchy for signageteammember login
    const fetchParentHierarchy = async () => {
        if (!isSignageTeammember || !token) return;
        
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                
                // Find the parent with license_type "SIGNAGE_FIRM"
                const signageFirm = data.parent_chain?.find(
                    (parent: any) => parent.license_type === "SIGNAGE_FIRM"
                );
                
                if (signageFirm) {
                    setParentSignageId(signageFirm.id || signageFirm._id);
                    setParentSignageName(signageFirm.name);
                }
            }
        } catch (error) {
            console.error("Error fetching parent hierarchy:", error);
        }
    };

    // Fetch parent hierarchy for signageteammember login
    useEffect(() => {
        if (isSignageTeammember && token) {
            fetchParentHierarchy();
        }
    }, [isSignageTeammember, token]);
    
    // Determine which signage ID to use for API calls
    const effectiveSignageId = (isSignageTeammember && parentSignageId) ? parentSignageId : userId;
    
    // Helper function and constants for AsyncPaginate
    const normalizeId = (row: any) => String(row?.id ?? row?._id ?? "");
    const PAGE_SIZE = 20;
    
    // ===== Brokerage Firm Loader =====
    const loadBrokerageOptions: LoadOptions<BrokerageOption, any, { page: number }> = useCallback(
        async (inputValue, _loadedOptions, additional) => {
            const page = additional?.page ?? 1;

            // For signageteammember, wait for parentSignageId to be available
            // If it's not available yet, wait a bit and retry
            if (isSignageTeammember && !parentSignageId) {
                // Wait a short time for parentSignageId to be loaded
                await new Promise(resolve => setTimeout(resolve, 100));
                // If still not available after waiting, return empty
                if (!parentSignageId) {
                    return { options: [], hasMore: false, additional: { page } };
                }
            }

            try {
                const q = (inputValue || "").trim();
                // Use parentSignageId for signageteammember, userId for signage
                // Recalculate effectiveSignageId here to get the latest value
                const signageFirmID = (isSignageTeammember && parentSignageId) ? parentSignageId : userId;

                const url =
                    `${process.env.REACT_APP_API_URL}/users-search` +
                    `?licenseType=BROKERAGE_FIRM` +
                    `&signageFirmID=${encodeURIComponent(signageFirmID || "")}` +
                    `&page=${page}&limit=${PAGE_SIZE}` +
                    (q ? `&search=${encodeURIComponent(q)}` : "");

                console.log('Loading brokerage options:', { page, q, signageFirmID, url });

                const response = await fetch(url, {
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    console.error("Failed to fetch brokerage firms:", response.statusText);
                    return { options: [], hasMore: false, additional: { page } };
                }

                const json = await response.json();
                console.log('Brokerage API response:', { page, q, json });

                const list = Array.isArray(json) ? json : json.data ?? json.results ?? [];

                const options: BrokerageOption[] = list.map((u: any) => ({
                    value: normalizeId(u),
                    label: u?.firmDetails?.companyName || u?.text || "(no name)",
                    firstName: u?.firstName || "",
                    lastName: u?.lastName || "",
                    email: u?.email || "",
                    phone: u?.phone1 || "",
                }));

                const hasMore = Array.isArray(json)
                    ? options.length === PAGE_SIZE
                    : json?.pagination?.more ??
                        (json?.total ? page * (json?.limit ?? PAGE_SIZE) < json.total : options.length === PAGE_SIZE);

                return {
                    options,
                    hasMore,
                    additional: { page: page + 1 },
                };
            } catch (err) {
                console.error("Failed to load brokerage options:", err);
                return { options: [], hasMore: false, additional: { page } };
            }
        },
        [isSignageTeammember, parentSignageId, effectiveSignageId, token]
    );
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [globalFilter, setGlobalFilter] = useState("");
    const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
    const [exporting, setExporting] = useState(false);

    // Pagination
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [rowCount, setRowCount] = useState(0);
    
    // Calculate date range limits (last 6 months from today)
    const dateRangeLimits = useMemo(() => {
        const today = new Date();
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(today.getMonth() - 6);
        
        // Format as YYYY-MM-DD for date input
        const formatDate = (date: Date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        return {
            minDate: formatDate(sixMonthsAgo),
            maxDate: formatDate(today)
        };
    }, []);

    // Validate and reset dates when custom is selected or dates are out of range
    useEffect(() => {
        if (datePreset.value === "custom") {
            const minDate = dateRangeLimits.minDate;
            const maxDate = dateRangeLimits.maxDate;
            
            // Reset From date if it's outside the valid range
            if (customFrom && (customFrom < minDate || customFrom > maxDate)) {
                setCustomFrom("");
            }
            
            // Reset To date if it's outside the valid range
            if (customTo && (customTo < minDate || customTo > maxDate)) {
                setCustomTo("");
            }
        }
    }, [datePreset.value, dateRangeLimits, customFrom, customTo]);

    // For Generate Button
    const [generateClicked, setGenerateClicked] = useState(false);

    const navigate = useNavigate();

    const fmtMoney = (n: number) =>
        new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(Number(n || 0));


    // Fetch data when Generate Button is clicked
    useEffect(() => {
        if (generateClicked) {
            fetchData();
        }
        else {
            fetchData();
        }
        // eslint-disable-next-line
    }, [generateClicked, pagination, report, globalFilter, location.pathname]);

    useEffect(() => {
        setPagination((prev) =>
            prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
        );
    }, [report, location.pathname]);

    function handleGenerate() {
        setPagination({ ...pagination, pageIndex: 0 });
        setGenerateClicked(true);
    }

    // Function to fetch direct signage agents
    const fetchSignageAgents = async () => {
        // For signageteammember, wait for parentSignageId to be available
        if (isSignageTeammember && !parentSignageId) {
            return;
        }

        try {
            // Use parentSignageId for signageteammember, userId for signage
            const signageFirmID = effectiveSignageId;
            
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/users/signage/${signageFirmID}/agents`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch signage agents");
            }

            const data = await response.json();
            setSignageAgentOptions(data.map((agent: any) => ({ 
                label: `${agent.firstName} ${agent.lastName}`, 
                value: agent.id 
            })));
        } catch (err) {
            setError(err instanceof Error ? err.message : "An unknown error occurred");
            console.error("Error fetching signage agents:", err);
        }
    };

    // Function to clear all filters
    const handleClearFilters = useCallback(() => {
        setDatePreset(DATE_OPTIONS[2]); // Last 30 Days
        setCustomFrom("");
        setCustomTo("");
        setPaymentStatus(PAYMENT_STATUS_OPTIONS[0]); // All
        setPaymentMethod([PAYMENT_METHOD_OPTIONS[0]]); // Card
        setSelectedBrokerageOption(null);
        setSelectedOffice(null);
        setSelectedAgent(null);
        setGlobalFilter("");
        setSearchInput("");
        setPagination({ pageIndex: 0, pageSize: 10 });
        setGenerateClicked(true);
    }, []);

    async function fetchData() {
        setLoading(true);
        try {
            const endpoint = report === "installation_summary" ? "reports/signage/installation_summary/datatable" : "reports/signage/sales_order_summary";
            const res = await fetch(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    limit: pagination.pageSize,
                    page: pagination.pageIndex + 1,
                    date_range: {
                        type: datePreset.value,
                        from: datePreset.value === "custom" ? customFrom : "",
                        to: datePreset.value === "custom" ? customTo : "",
                    },
                    search: globalFilter || "",
                    payment_status: paymentStatus.value === "all" ? [] : [paymentStatus.value],
                    // payment_method: paymentMethod.map((m: any) => m.value),
                    brokerage_firm_id: selectedBrokerageOption?.value,
                    office_id: selectedOffice?.value,
                    agent_id: selectedAgent?.value,
                }),
            });
            const result = await res.json();
            // console.log("Installation Summary Report Data:", result);
            setData(result.data || []);
            setCards(result.cards || null);
            setRowCount(result.total || 0);
        } catch (err) {
            console.error("Error fetching installation summary:", err);
            setData([]);
            setCards(null);
            setRowCount(0);
        } finally {
            setLoading(false);
            setGenerateClicked(false);
        }
    }

    const handleExportCsv = async () => {
        if (!token) return;
        try {
            setExporting(true);
            const endpoint = report === "installation_summary"
                ? "reports/signage/installation_summary/export"
                : "reports/signage/sales_order_summary/export";
            const payload = {
                page: 1,
                limit: rowCount || 10000,
                search: globalFilter ?? "",
                payment_status: paymentStatus.value === "all" ? [] : [paymentStatus.value],
                // payment_method: paymentMethod?.map((m: any) => m?.value) ?? [],
                brokerage_firm_id: selectedBrokerageOption?.value ?? null,
                office_id: selectedOffice?.value ?? null,
                agent_id: selectedAgent?.value ?? null,
                agent_name: "",
                date_range: {
                    type: datePreset?.value ?? "",
                    from: datePreset?.value === "custom" ? customFrom : "",
                    to: datePreset?.value === "custom" ? customTo : "",
                },
            };
            const response = await fetch(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
                method: "POST",
                credentials: "omit",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                console.error("Download failed:", response.statusText);
                toast.error("Export failed");
                return;
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = response.headers.get("Content-Disposition")?.split("filename=")[1]?.replace(/"/g, "") || (report === "installation_summary" ? "installation_summary.csv" : "sales_order_summary.csv");
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            toast.success("Export downloaded");
        } catch (err: any) {
            console.error("Export error:", err);
            toast.error(err?.message || "Failed to export");
        } finally {
            setExporting(false);
        }
    };

    // Column Definitions
    const columnHelper = createColumnHelper<InstallationSummaryRow>();

    const columns = useMemo(() => {
        const baseColumns = [
            columnHelper.display({
                id: "serial",
                header: () => <div className="th-text S-No">S.No.</div>,
                cell: (info) => (
                    <div className="text-sm S-No">
                        {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
                    </div>
                ),
            }),
            columnHelper.accessor("brokerage_firm", {
                header: () => <div className="th-text ">Brokerage Firm</div>,
                cell: (info) => {
                    const value = info.getValue();
                    return (
                        <div className="fw-medium text-sm">
                            {value === "Unassigned" || !value ? "--" : value}
                        </div>
                    );
                },
            }),
            columnHelper.accessor("office", {
                header: () => <div className="th-text ">Office</div>,
                cell: (info) => (
                    <div className="text-sm">{info.getValue() || "--"}</div>
                ),
            }),
            columnHelper.accessor("office_admin", {
                header: () => <div className="th-text ">Office Admin</div>,
                cell: (info) => (
                    <div className="text-sm">{info.getValue() || "--"}</div>
                ),
            }),
            columnHelper.accessor("agent_name", {
                header: () => <div className="th-text ">Agent Name</div>,
                cell: (info) => (
                    <div className="text-sm">{info.getValue() || "--"}</div>
                ),
            }),
        ];

        const dynamicColumn =
            report === "installation_summary"
                ? columnHelper.accessor("installations", {
                    header: () => <div className="th-text ">Installations</div>,
                    cell: (info) => (
                        <div className="text-sm">{info.getValue() ?? 0}</div>
                    ),
                })
                : columnHelper.accessor("sales_orders", {
                    header: () => <div className="th-text ">Sales Orders</div>,
                    cell: (info) => (
                        <div className="text-sm">{info.getValue() ?? 0}</div>
                    ),
                });

        const restColumns = [
            columnHelper.accessor("total_amount", {
                header: () => <div className="th-text ">Total Amount</div>,
                cell: (info) => (
                    <div className="fw-medium text-sm">
                        ${Number(info.getValue() ?? 0).toFixed(2)}
                    </div>
                ),
            }),
            columnHelper.accessor("paid", {
                header: () => <div className="th-text ">Paid</div>,
                cell: (info) => (
                    <div className="text-sm" style={{color :"#00a63e"}}>
                        ${Number(info.getValue() ?? 0).toFixed(2)}
                    </div>
                ),
            }),
            columnHelper.accessor("balance", {
                header: () => <div className="th-text ">Balance</div>,
                cell: (info) => {
                    const overPaid = info.row.original.over_paid;
                    const balanceValue = Number(info.getValue() ?? 0).toFixed(2);
                    return (
                        <div className={`text-sm fw-medium`} style={{ color: overPaid ? "#00a63e" : "#e7000b" }}>
                            {overPaid ? `+$${balanceValue}` : `-$${balanceValue}`}
                        </div>
                    );
                },
            }),
        ];

        return [
            ...baseColumns,
            dynamicColumn,
            ...restColumns,
        ];
    }, [columnHelper, report, pagination]);


    // const columns = useMemo(() => {
    //     const baseColumns = [
    //         columnHelper.display({
    //             id: "serial",
    //             header: () => <div className="text-center w-100">S.No.</div>,
    //             cell: (info) => (
    //                 <div className="text-center w-100">
    //                     {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
    //                 </div>
    //             ),
    //         }),
    //         columnHelper.accessor("brokerage_firm", { header: "Brokerage Firm", cell: info => info.getValue() || "--" }),
    //         columnHelper.accessor("office", { header: "Office", cell: info => info.getValue() || "--" }),
    //         columnHelper.accessor("office_admin", { header: "Office Admin", cell: info => info.getValue() || "--" }),
    //         columnHelper.accessor("agent_name", { header: "Agent Name", cell: info => info.getValue() || "--" }),
    //     ];

    //     const dynamicColumn =
    //         report === "installation_summary"
    //             ? columnHelper.accessor("installations", { header: "Installations", cell: info => info.getValue() ?? 0 })
    //             : columnHelper.accessor("sales_orders", { header: "Sales Orders", cell: info => info.getValue() ?? 0 });

    //     const restColumns = [
    //         columnHelper.accessor("total_amount", { header: "Total Amount", cell: info => `$${Number(info.getValue() ?? 0).toFixed(2)}` }),
    //         columnHelper.accessor("paid", { header: "Paid", cell: info => `$${Number(info.getValue() ?? 0).toFixed(2)}` }),
    //         columnHelper.accessor("balance", { header: "Balance", cell: info => `$${Number(info.getValue() ?? 0).toFixed(2)}` }),
    //     ];

    //     return [
    //         ...baseColumns,
    //         dynamicColumn,
    //         ...restColumns
    //     ];
    // }, [columnHelper, report]);


    // Table instance
    const table = useReactTable({
        data: data || [],
        columns,
        pageCount: Math.ceil(rowCount / pagination.pageSize),
        state: { pagination },
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
        onPaginationChange: setPagination,
    });

    // Fetch signage agents on component mount
    useEffect(() => {
        fetchSignageAgents();
    }, [token, effectiveSignageId, isSignageTeammember, parentSignageId]);

    // Removed fetchBrokerageFirms useEffect - now using AsyncPaginate

    useEffect(() => {
        if (selectedBrokerageOption) {
            // Clear agent and office selection when brokerage is selected
            setSelectedAgent(null);
            setSelectedOffice(null);
            
            const fetchBrokerageOffice = async () => {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/office/${selectedBrokerageOption.value}`,
                        {
                            method: "GET",
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            },
                        }
                    );

                    const data = await response.json();
                    // console.log("fetchBrokerageFirms data :", data);

                    if (!response.ok) {
                        // throw new Error("Failed to fetch agents");
                        toast.error(data.error || "Failed to fetch offices");
                    }

                    setOfficeOptions(data.map((i: any) => ({ label: i.officeName, value: i._id })));
                    // console.log("Office based on the selected brokerage:", data);
                } catch (err) {
                    setError(
                        err instanceof Error ? err.message : "An unknown error occurred"
                    );
                    console.error("Error fetching brokerage firms:", err);
                }
            };
            fetchBrokerageOffice();
        } else {
            // Clear office options when brokerage is deselected
            setOfficeOptions([]);
            setAgentOptions([]);
        }
    }, [selectedBrokerageOption]);

    useEffect(() => {
        handleClearFilters();
    }, [location.pathname, handleClearFilters]);

    useEffect(() => {
        if (selectedOffice) {
            const fetchOfficeAgents = async () => {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/agent/office/${selectedOffice.value}`,
                        {
                            method: "GET",
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            },
                        }
                    );
                    const data = await response.json();
                    console.log("officeAgents :", data);

                    if (!response.ok) {
                        toast.error(data.error || "Failed to fetch agents");
                    } else {
                        setAgentOptions(data.map((i: any) => ({ label: `${i.firstName} ${i.lastName}`, value: i.id })));
                        // console.log("Office Agents:", data);
                    }
                } catch (err) {
                    setError(
                        err instanceof Error ? err.message : "An unknown error occurred"
                    );
                }
            };
            fetchOfficeAgents();
        }
    }, [selectedOffice]);

    return (
        <>
            <div className="content">
                <div className="mb-4">
                    {
                        report === "installation_summary" ? (
                            <>
                                <div>
                                    <h4 className="mb-1 po-title">Installations Billing & Summary</h4>
                                    <p className="po-subtitle">Track installation billing and payment status across your organization</p>
                                </div>
                            </>
                        ) : (
                            <>
                                <div>
                                    <h4 className="mb-1 po-title">Sales Order Summary</h4>
                                    <p className="po-subtitle">Track and analyze sales performance across your organization</p>
                                </div>
                            </>

                        )
                    }
                </div>
                <div className="row g-3 mb-4">
                    <div className="col-12 col-sm-6 col-xl-3">
                        <StatCard
                            label={`${report === "installation_summary" ? "Total Installations" : "Total Orders"}`}
                            value={cards?.total_orders ?? 0}
                            money={false}
                            iconName="totalOrders"
                        />
                    </div>

                    <div className="col-12 col-sm-6 col-xl-3">
                        <StatCard
                            label="Total Revenue"
                            value={cards?.total_revenue ?? 0}
                            money
                            iconName="totalRevenue"
                        />
                    </div>

                    <div className="col-12 col-sm-6 col-xl-3">
                        <StatCard
                            label="Amount Paid"
                            value={cards?.amount_paid ?? 0}
                            money
                            iconName="paidBill"
                        />
                    </div>

                    <div className="col-12 col-sm-6 col-xl-3">
                        <StatCard
                            label="Outstanding Balance"
                            value={cards?.outstanding_balance ?? 0}
                            money
                            iconName="outstandingBalance"
                        />
                    </div>
                </div>

                <div className="po-header mb-4 shadow-border-datatables">
                    {/* TOP ROW */}
                    <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
                        <div className="po-title-wrap me-2">
                            {/* <h4 className="mb-1 po-title">Incoming Agent Products</h4>
                            <div className="po-subtitle">Manage incoming products from suppliers and brokerages</div> */}
                        </div>

                        {/* tools block (search + buttons) */}
                        <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
                            {/* Search */}
                            <div className="input-group po-search ">
                                <span className="input-group-text">
                                    <i className="ri-search-line" />
                                </span>
                                <input
                                    type="text"
                                    id="searchInput"
                                    name="searchInput"
                                    className="form-control"
                                    placeholder="Search by Agent, Office, Brokerage Firm ..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") setGlobalFilter(searchInput); }}
                                    autoComplete="off"
                                />
                            </div>

                            {/* Filters toggle */}
                            <button
                                type="button"
                                className={` po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
                                onClick={() => setFiltersOpen(v => !v)}
                                aria-expanded={filtersOpen}
                                aria-controls="incoming-filters"
                            >
                                <i className="ri-filter-3-line me-2" />
                                <span>Filters</span>
                                <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
                            </button>

                            <button
                                type="button"
                                className="export-button"
                                onClick={handleExportCsv}
                                disabled={exporting}
                            >
                                <i className="ri-file-excel-2-line me-2" />
                                {exporting ? "Exporting…" : "Export to Excel"}
                            </button>
                        </div>
                    </div>

                    <div id="incoming-filters" className={`po-filters ${filtersOpen ? "show" : ""}`} style={{ overflow: "visible" }}>
                        <div className="po-filters-bar d-flex" style={{ overflow: "visible" }}>
                            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto" style={{ overflow: "visible" }}>

                                <div className="col-lg-2" style={{ position: "relative" }}>
                                    <label className="form-label mb-1 label-1">Date Range</label>
                                    <Select options={DATE_OPTIONS} value={datePreset}
                                        onChange={val => val && setDatePreset(val)} components={{ DropdownIndicator }}
                                        styles={customSelectStyles}
                                        classNamePrefix="rs" />
                                </div>
                                {datePreset.value === "custom" && (
                                    <>
                                        <div className="col-lg-2">
                                            <label className="form-label mb-1 label-1">From</label>
                                            <input 
                                                type="date" 
                                                className="form-control" 
                                                value={customFrom}
                                                onChange={e => setCustomFrom(e.target.value)}
                                                min={dateRangeLimits.minDate}
                                                max={dateRangeLimits.maxDate}
                                            />
                                        </div>
                                        <div className="col-lg-2">
                                            <label className="form-label mb-1 label-1">To</label>
                                            <input 
                                                type="date" 
                                                className="form-control" 
                                                value={customTo}
                                                onChange={e => setCustomTo(e.target.value)}
                                                min={dateRangeLimits.minDate}
                                                max={dateRangeLimits.maxDate}
                                            />
                                        </div>
                                    </>
                                )}

                                <div className="col-lg-1" style={{ position: "relative" }}>
                                    <label className="form-label mb-1 label-1">Payment Status</label>
                                    <Select options={PAYMENT_STATUS_OPTIONS} value={paymentStatus}
                                        onChange={val => val && setPaymentStatus(val)} components={{ DropdownIndicator }}
                                        styles={customSelectStyles}
                                        classNamePrefix="rs" />
                                </div>

                                <div className="col-lg-2" style={{ position: "relative" }}>
                                    <label className="form-label mb-1 label-1">Brokerage Firm</label>
                                    <AsyncPaginate
                                        classNamePrefix="rs"
                                        placeholder={isSignageTeammember && !parentSignageId ? "Loading..." : "Search brokerage firms..."}
                                        value={selectedBrokerageOption}
                                        loadOptions={loadBrokerageOptions}
                                        onChange={(selected) => {
                                            setSelectedBrokerageOption(selected);
                                        }}
                                        additional={{ page: 1 }}
                                        defaultOptions={!isSignageTeammember || parentSignageId ? true : false}
                                        debounceTimeout={300}
                                        // isClearable
                                        isDisabled={isSignageTeammember && !parentSignageId}
                                        components={{ DropdownIndicator }}
                                        styles={customSelectStyles}
                                    />
                                </div>
                                <div className="col-lg-1" style={{ position: "relative" }}>
                                    <label className="form-label mb-1 label-1">Office</label>
                                    <Select options={officeOptions} value={selectedOffice}
                                        onChange={val => setSelectedOffice(val ?? null)} isSearchable isDisabled={!selectedBrokerageOption} components={{ DropdownIndicator }}
                                        styles={customSelectStyles}
                                        classNamePrefix="rs" />
                                </div>
                                <div className="col-lg-2" style={{ position: "relative" }}>
                                    <label className="form-label mb-1 label-1">Agent</label>
                                    <Select 
                                        options={selectedBrokerageOption ? agentOptions : signageAgentOptions} 
                                        value={selectedAgent}
                                        onChange={val => setSelectedAgent(val ?? null)} 
                                        isSearchable 
                                        isDisabled={selectedBrokerageOption ? !selectedOffice : false} 
                                        components={{ DropdownIndicator }}
                                        styles={agentSelectStyles}
                                        classNamePrefix="rs" 
                                    />
                                </div>

                                <div className="col-lg-auto d-flex align-items-end gap-2">
                                    <button className="btn btn-primary table-addbutton flex-fill flex-md-grow-0"
                                        onClick={handleGenerate}>
                                        Apply Filter
                                    </button>
                                    <button className="btn back-btn surface-btn fw-bold fs-16 flex-fill flex-md-grow-0"
                                        onClick={handleClearFilters}>
                                        Clear
                                    </button>
                                </div>

                                {/* <div className="col-12 col-md-auto d-flex justify-content-md-end gap-2 mt-1 mt-md-0">
                                    <button
                                        className="btn btn-primary flex-fill flex-md-grow-0"
                                        onClick={() => {
                                            // setLinkedStatusFilter(tempLinkedStatus);
                                            // setCurrentStatusFilter(tempCurrentStatus);
                                        }}
                                    >
                                        <i className="ri-check-line me-1" />
                                        Apply Filter
                                    </button>

                                    <button
                                        className="btn btn-light  flex-fill flex-md-grow-0 clear-button"
                                        onClick={() => {
                                            // setTempLinkedStatus("all");
                                            // setTempCurrentStatus("all");
                                            // setLinkedStatusFilter("all");
                                            // setCurrentStatusFilter("all");
                                        }}
                                    >
                                        Clear
                                    </button>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
                    <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
                        <div className="d-flex align-items-center">
                            <i
                                className="ri-file-text-line me-2"
                                aria-hidden="true"
                                style={{ fontSize: 18, color: "#2563eb", lineHeight: 1 }}
                            />
                            <h5 className="mb-0">{report === "installation_summary" ? "Installation Data" : "Sales Order Summary Data"}</h5>
                        </div>
                    </div>
                    <hr className="mb-2" style={{ borderTop: "none" }} />


                    {/* DESKTOP/TABLET */}
                    <div className="rdt-desktop">
                        <div
                            className="production-scroll"
                            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
                        >
                            <table className="table production-table align-middle mb-0 table-1">
                                {loading ? (
                                    <DesktopTableLoader table={table} message="Loading report data…" />
                                ) : (
                                    <DesktopTableParts table={table} />
                                )}
                            </table>
                        </div>
                    </div>

                    {/* MOBILE (≤ 1021px) */}
                    <div className="rdt-mobile">
                        {loading ? (
                            <MobileCardsLoader message="Loading report data…" />
                        ) : (
                            <MobileCardsOnly
                            table={table}
                            idColId="order_link"
                            titleColId="product_name"
                            actionColId="actions"
                        />
                        )}
                    </div>

                </div>
                <DataTableStatus
                    loading={loading}
                    isEmpty={table.getRowModel().rows.length === 0}
                >
                    <div className="mt-4 mb-4">
                        <TablePaginationControls table={table} />
                    </div>
                </DataTableStatus>
            </div >
        </>
    )
}
